"use client";

import { useId, useState, useSyncExternalStore, type FormEvent } from "react";

import { parseBirthDate, type BirthDate, type BirthDateProblem } from "@/lib/numerology-digits";
import { LifePathDobResult, MulankDobResult } from "./NumerologyDobCheck.result";

/**
 * Inline date-of-birth check for /numerology/<1-9>/ and /numerology/life-path/.
 *
 * Pure digit arithmetic, computed in the browser on submit: no network, no
 * name, no offer or chat entry (the pages keep their one offer and one chat
 * entry, tests/offer-stacking-contract.test.ts). Server pages mount it only
 * through NumerologyDobCheck.lazy.tsx.
 *
 * The server HTML carries no date: there is no max={today}, and the client
 * clock is read only inside the submit handler, so /numerology/life-path/ stays
 * a reviewed date-free ISR page and hydration has nothing to disagree about.
 * No analytics in v1: the only calculator_used emitter (trackCalculatorUsed)
 * also fires free_tool_completed, which the owner kept out of this placement.
 */

export type NumerologyDobCheckProps =
  | {
      variant: "mulank";
      pageMulank: number;
      /** Ruling planet per Mulank, from the server page's governed profiles. */
      planetByMulank: Readonly<Record<number, string>>;
    }
  | { variant: "life-path" };

const PROBLEM_MESSAGE: Record<BirthDateProblem, string> = {
  empty: "Enter your date of birth first.",
  invalid: "That date does not exist. Check the day, month and year.",
  "too-early": "Enter a date of birth from 1900 onwards.",
  future: "That date is in the future. Enter your date of birth.",
};

const subscribeToNothing = () => () => {};

/**
 * False in the server HTML and during hydration, true once React owns the
 * form. The input has no name, and the button stays disabled until then, so a
 * submit before hydration cannot fall through to a native GET that reloads the
 * page and drops the date.
 */
function useHydrated(): boolean {
  return useSyncExternalStore(
    subscribeToNothing,
    () => true,
    () => false
  );
}

function copyFor(props: NumerologyDobCheckProps) {
  if (props.variant === "mulank") {
    return {
      eyebrow: "Check your Mulank",
      title: `Is Mulank ${props.pageMulank} your number?`,
      submit: "Check my number",
      caption:
        "Mulank comes from the day you were born; Bhagyank adds every digit of the full date.",
    };
  }
  return {
    eyebrow: "Calculate your Life Path",
    title: "Find your Life Path number",
    submit: "Calculate",
    caption:
      "Every digit of the date is added and reduced; a total that reaches 11, 22 or 33 stays a master number.",
  };
}

function DobResult({ props, birth }: { props: NumerologyDobCheckProps; birth: BirthDate }) {
  if (props.variant === "life-path") return <LifePathDobResult birth={birth} />;
  return (
    <MulankDobResult
      birth={birth}
      pageMulank={props.pageMulank}
      planetByMulank={props.planetByMulank}
    />
  );
}

export default function NumerologyDobCheck(props: NumerologyDobCheckProps) {
  const id = useId();
  const hydrated = useHydrated();
  const [value, setValue] = useState("");
  const [problem, setProblem] = useState<BirthDateProblem | null>(null);
  const [birth, setBirth] = useState<BirthDate | null>(null);
  const copy = copyFor(props);
  const titleId = `${id}-title`;
  const inputId = `${id}-dob`;
  const captionId = `${id}-caption`;

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const parsed = parseBirthDate(value);
    setProblem(parsed.ok ? null : parsed.problem);
    setBirth(parsed.ok ? parsed.date : null);
  }

  return (
    <div className="card-sacred mt-6 p-4 sm:p-5" data-numerology-dob-check={props.variant}>
      <p className="text-eyebrow font-semibold uppercase tracking-wider text-intent-brand">
        {copy.eyebrow}
      </p>
      <p id={titleId} className="mt-1 font-serif text-subtitle-small font-bold text-text-primary">
        {copy.title}
      </p>
      <form
        translate="no"
        noValidate
        aria-labelledby={titleId}
        onSubmit={handleSubmit}
        className="mt-3"
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="sm:flex-1">
            <label
              htmlFor={inputId}
              className="block text-body-small font-semibold text-text-primary"
            >
              Date of birth
            </label>
            <input
              id={inputId}
              type="date"
              min="1900-01-01"
              autoComplete="bday"
              value={value}
              onChange={(event) => setValue(event.target.value)}
              aria-describedby={captionId}
              aria-invalid={problem ? true : undefined}
              className="input-sacred mt-1 w-full rounded-lg px-3 py-2.5 text-body text-text-primary"
            />
          </div>
          <button
            type="submit"
            disabled={!hydrated}
            className="btn-sacred rounded-lg px-5 py-2.5 text-body font-semibold"
          >
            {copy.submit}
          </button>
        </div>
        <p id={captionId} className="mt-2 text-caption text-text-muted">
          {copy.caption}
        </p>
        {problem ? (
          <p role="alert" className="mt-3 text-body-small text-error">
            {PROBLEM_MESSAGE[problem]}
          </p>
        ) : null}
        {/* Present and empty from first paint, so the result is announced
            when it fills the region rather than arriving with it. */}
        <div aria-live="polite">{birth ? <DobResult props={props} birth={birth} /> : null}</div>
      </form>
    </div>
  );
}
